import { Menu, X } from 'lucide-react'
import { useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { getScenarioOptions } from '@/domain/yjmall/scenarios'
import type { ScenarioId } from '@/domain/yjmall/types'
import { I18N_KEYS } from '@/i18n/keys'
import { cn } from '@/lib/cn'
import { useYJMockStore, useYJState } from '@/stores/yjmall-mock-store'

type NavItem = {
  labelKey: string
  to: string
  active: (pathname: string) => boolean
}

const portalNavItems: NavItem[] = [
  {
    labelKey: I18N_KEYS.nav.admin,
    to: '/a1',
    active: (pathname) => pathname.startsWith('/a'),
  },
  {
    labelKey: I18N_KEYS.nav.supplier,
    to: '/b',
    active: (pathname) => pathname.startsWith('/b'),
  },
  {
    labelKey: I18N_KEYS.nav.affiliate,
    to: '/c',
    active: (pathname) => pathname.startsWith('/c'),
  },
  {
    labelKey: I18N_KEYS.nav.niceCall,
    to: '/d',
    active: (pathname) => pathname.startsWith('/d'),
  },
  {
    labelKey: I18N_KEYS.nav.customer,
    to: '/e',
    active: (pathname) => pathname.startsWith('/e'),
  },
]

const adminTabs: NavItem[] = [
  {
    labelKey: I18N_KEYS.nav.cofounder,
    to: '/a1',
    active: (pathname) => pathname.startsWith('/a1'),
  },
  {
    labelKey: I18N_KEYS.nav.marketing,
    to: '/a2',
    active: (pathname) => pathname.startsWith('/a2'),
  },
  {
    labelKey: I18N_KEYS.nav.support,
    to: '/a3',
    active: (pathname) => pathname.startsWith('/a3'),
  },
]

function navLinkClass(isActive: boolean) {
  return cn(
    'inline-flex h-10 items-center rounded-md border px-3 text-sm font-medium transition',
    isActive
      ? 'border-primary bg-primary text-primary-foreground shadow-xs'
      : 'border-line bg-surface text-muted-foreground hover:border-primary/30 hover:bg-primary-muted hover:text-primary',
  )
}

function tabLinkClass(isActive: boolean) {
  return cn(
    'inline-flex h-9 items-center border-b-2 px-1 text-sm font-medium transition',
    isActive ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:border-line hover:text-foreground',
  )
}

const scenarioLabels: Record<ScenarioId, string> = {
  normal: 'ปกติ',
  'high-gmv': 'GMV สูง',
  'lead-backlog': 'ลีดค้าง',
  'low-stock': 'สต็อกต่ำ',
  'supplier-dispute': 'ข้อพิพาทแบรนด์',
  'payout-pending': 'รอจ่ายเงิน',
  'campaign-spike': 'แคมเปญพุ่ง',
  'payment-fail': 'ชำระเงินล้มเหลว',
  'pdpa-decline': 'ไม่ยินยอม PDPA',
}

export function AppShell() {
  const { pathname } = useLocation()
  const { t } = useTranslation()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const currentScenario = useYJState((state) => state.currentScenario)
  const loadScenario = useYJMockStore((state) => state.loadScenario)
  const isAdminRoute = pathname.startsWith('/a')
  const scenarioOptions = getScenarioOptions()

  const closeMobileMenu = () => setIsMobileMenuOpen(false)
  const onScenarioChange = (value: string) => {
    loadScenario(value as ScenarioId)
    closeMobileMenu()
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 border-b border-line bg-surface/95 backdrop-blur">
        <div className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <NavLink
            to="/a1"
            className="flex items-center gap-3"
            data-testid="app-shell-logo"
            onClick={closeMobileMenu}
          >
            <span className="grid h-10 w-10 place-items-center rounded-lg bg-primary text-base font-semibold text-primary-foreground shadow-xs">
              YJ
            </span>
            <span className="min-w-0">
              <span className="block text-base font-semibold text-foreground">{t(I18N_KEYS.app.name)}</span>
              <span className="hidden text-xs text-muted-foreground sm:block">{t(I18N_KEYS.app.subtitle)}</span>
            </span>
          </NavLink>

          <nav className="hidden items-center gap-2 lg:flex" aria-label="Portal navigation">
            {portalNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={navLinkClass(item.active(pathname))}
                data-testid={`app-shell-nav-${item.to.replace('/', '')}`}
              >
                {t(item.labelKey)}
              </NavLink>
            ))}
          </nav>

          <button
            type="button"
            className="inline-grid h-10 w-10 place-items-center rounded-md border border-line bg-surface text-muted-foreground lg:hidden"
            aria-label={t(isMobileMenuOpen ? I18N_KEYS.nav.close : I18N_KEYS.nav.menu)}
            aria-expanded={isMobileMenuOpen}
            data-testid="app-shell-menu-toggle"
            onClick={() => setIsMobileMenuOpen((current) => !current)}
          >
            {isMobileMenuOpen ? <X aria-hidden="true" size={18} /> : <Menu aria-hidden="true" size={18} />}
          </button>

          <div className="hidden items-center gap-2 lg:flex">
            <label htmlFor="scenario-switcher" className="text-xs font-medium text-muted-foreground">
              Scenario
            </label>
            <select
              id="scenario-switcher"
              value={currentScenario}
              onChange={(event) => onScenarioChange(event.target.value)}
              className="h-10 rounded-md border border-line bg-surface px-3 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
              data-testid="app-shell-scenario-select"
            >
              {scenarioOptions.map((scenario) => (
                <option key={scenario.id} value={scenario.id}>
                  {scenarioLabels[scenario.id]}
                </option>
              ))}
            </select>
          </div>
        </div>

        {isMobileMenuOpen ? (
          <nav
            className="grid gap-2 border-t border-line px-4 py-3 sm:px-6 lg:hidden"
            aria-label="Mobile portal navigation"
            data-testid="app-shell-mobile-nav"
          >
            {portalNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={navLinkClass(item.active(pathname))}
                onClick={closeMobileMenu}
                data-testid={`app-shell-mobile-nav-${item.to.replace('/', '')}`}
              >
                {t(item.labelKey)}
              </NavLink>
            ))}
            <select
              value={currentScenario}
              onChange={(event) => onScenarioChange(event.target.value)}
              className="h-10 rounded-md border border-line bg-surface px-3 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
              data-testid="app-shell-mobile-scenario-select"
            >
              {scenarioOptions.map((scenario) => (
                <option key={scenario.id} value={scenario.id}>
                  {scenarioLabels[scenario.id]}
                </option>
              ))}
            </select>
          </nav>
        ) : null}

        {isAdminRoute ? (
          <div className="border-t border-line bg-surface" data-testid="app-shell-admin-tabs">
            <nav
              className="mx-auto flex w-full max-w-7xl gap-5 overflow-x-auto px-4 sm:px-6 lg:px-8"
              aria-label="Admin sub navigation"
            >
              {adminTabs.map((item) => (
                <NavLink key={item.to} to={item.to} className={tabLinkClass(item.active(pathname))}>
                  {t(item.labelKey)}
                </NavLink>
              ))}
            </nav>
          </div>
        ) : null}
      </header>

      <Outlet />
    </div>
  )
}
